import { useState, useEffect } from 'react';
import { api } from '../api';

function formatBytes(n) {
  if (!n) return '0 B';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)} GB`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)} MB`;
  return `${(n / 1e3).toFixed(0)} KB`;
}

export function HardDeleteClientModal({ client, onClose, onSuccess }) {
  const [nameInput, setNameInput]   = useState('');
  const [deleteWasabi, setDeleteWasabi] = useState(false);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState('');
  const [wasabiSize, setWasabiSize] = useState(null);
  const [sizeLoading, setSizeLoading] = useState(true);

  useEffect(() => {
    api.getClientWasabiSize(client.id)
      .then(data => setWasabiSize(data))
      .catch(() => setWasabiSize({ size_bytes: 0, object_count: 0 }))
      .finally(() => setSizeLoading(false));
  }, [client.id]);

  const canConfirm = nameInput === client.name;

  const wasabiLabel = sizeLoading
    ? 'consultando...'
    : wasabiSize && wasabiSize.size_bytes > 0
      ? `${formatBytes(wasabiSize.size_bytes)}, ${wasabiSize.object_count} objeto(s)`
      : 'sin datos en Wasabi';

  const submit = async () => {
    setLoading(true); setError('');
    try {
      await api.hardDeleteClient(client.id, nameInput, deleteWasabi);
      onSuccess();
      onClose();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)' };
  const boxStyle     = { background: 'var(--surface)', border: '1px solid var(--border)', width: 480, maxHeight: '90vh', display: 'flex', flexDirection: 'column', clipPath: 'polygon(0 0, calc(100% - 20px) 0, 100% 20px, 100% 100%, 20px 100%, 0 calc(100% - 20px))' };

  return (
    <div style={overlayStyle}>
      <div style={boxStyle}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1, color: 'var(--red)' }}>ELIMINAR DEFINITIVAMENTE</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 18 }}>✕</button>
        </div>
        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          <div style={{ background: 'rgba(255,77,109,0.08)', border: '1px solid var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16, color: 'var(--red)' }}>
            ⚠ Esta operación es IRREVERSIBLE. Borra el cliente y todos sus servidores, jobs, runs y dependencias de la base de datos.
          </div>
          <div style={{ fontSize: 13, marginBottom: 16 }}>
            Cliente: <strong>{client.name}</strong>
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 16, cursor: 'pointer', padding: '12px', background: 'var(--bg)', border: '1px solid var(--border)' }}>
            <input type="checkbox" checked={deleteWasabi} onChange={e => setDeleteWasabi(e.target.checked)} style={{ marginTop: 2, cursor: 'pointer', accentColor: 'var(--red)' }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                Borrar datos en Wasabi ahora
                <span style={{ fontWeight: 400, color: 'var(--muted)', marginLeft: 6, fontSize: 12 }}>({wasabiLabel})</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--red)', fontFamily: 'var(--mono)' }}>⚠ Se borra el bucket completo (todos los servidores del cliente) y los usuarios IAM. Es irreversible.</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                Si no lo marcás, los datos igual se borran en 90 días (mínimo de facturación de Wasabi). No marcarlo pospone el borrado, no lo evita.
              </div>
            </div>
          </label>
          {error && <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>{error}</div>}
          <div>
            <label style={{ display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' }}>
              Escribí el nombre exacto del cliente para confirmar
            </label>
            <input
              type="text"
              value={nameInput}
              onChange={e => setNameInput(e.target.value)}
              placeholder={client.name}
              autoFocus
              style={{ width: '100%', background: 'var(--bg)', border: `1px solid ${canConfirm ? 'var(--red)' : 'var(--border)'}`, color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
            />
          </div>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
          <button onClick={onClose} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: 'pointer' }}>CANCELAR</button>
          <button onClick={submit} disabled={!canConfirm || loading} style={{ background: canConfirm ? 'var(--red)' : 'transparent', border: '1px solid var(--red)', color: canConfirm ? 'white' : 'var(--red)', padding: '7px 14px', fontSize: 12, fontFamily: 'var(--mono)', cursor: canConfirm ? 'pointer' : 'not-allowed', opacity: canConfirm ? 1 : 0.5, fontWeight: 700 }}>
            {loading ? 'ELIMINANDO...' : 'ELIMINAR DEFINITIVAMENTE'}
          </button>
        </div>
      </div>
    </div>
  );
}
